import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  Form,
  OkedSelectTrigger,
  SelectOption,
  StyledOkedListBlock,
  TextField,
} from '@app/components';
import * as yup from 'yup';
import { Oked, UserType } from '@app/models';
import { DN } from 'libs';
import { UserFormData } from '../User';
import { signUp } from '@app/api';
import Cookies from 'js-cookie';
import { parseFullName } from '@app/helpers';
import { useForm } from '@app/forms';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@app/providers';
import { useNavigate } from 'react-router-dom';

export interface CompanyFormData {
  okeds: Oked[];
  name: string;
  isSupplier: boolean;
}

interface CompanyFormProps {
  dn: DN;
  user: UserFormData;
}

function CompanyForm(props: CompanyFormProps) {
  const navigate = useNavigate();
  const { dn, user } = props;
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { values, onChange, errors, validate } = useForm<CompanyFormData>({
    name: !!dn.company ? dn.company.name : '',
    okeds: [],
    isSupplier: false,
  });
  const [pending, setPending] = useState<boolean>(false);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t('required', { ns: 'validation' })!),
      }),
    [t]
  );

  const onSupplierChange = (checked: boolean) => {
    onChange(checked, 'isSupplier');
  };

  const onChangeOked = useCallback(
    (data: SelectOption<Oked>) => {
      onChange([data.value, ...(values.okeds || [])], 'okeds');
    },
    [onChange, values.okeds]
  );

  const onClearOked = useCallback(
    (number: number) => {
      onChange(
        (values.okeds || []).filter((val, index) => index !== number),
        'okeds'
      );
    },
    [onChange, values.okeds]
  );

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const hasErrors = await validate(schema);

      if (hasErrors) {
        return;
      }

      setPending(true);

      const userNameData = parseFullName(user.fullName);

      signUp({
        user: {
          firstName: userNameData.firstName,
          lastName: userNameData.lastName,
          middleName: userNameData.middleName,
          email: user.email,
          phone: user.phone,
          password: user.password,
          type: UserType.ENTITY,
          katoCode: user.kato,
        },
        company: {
          bin: dn!.company!.bin,
          name: values.name,
          type: dn!.company!.type,
          okeds: values.okeds || [],
          isSupplier: values.isSupplier,
        },
      })
        .then((userResponse) => {
          navigate(`/email-verify?email=${encodeURIComponent(user.email)}`);
        })
        .catch((e) => {
          showNotification({
            variant: 'error',
            message:
              e?.response?.data?.message ??
              'Ошибка, обратитесь к администратору или попробуйте позднее',
          });

          setPending(false);
        });
    },
    [
      dn,
      schema,
      user.email,
      user.fullName,
      user.kato,
      user.password,
      user.phone,
      validate,
      values.name,
      values.okeds,
    ]
  );

  return (
    <Form
      onSubmit={onSubmitForm}
      title="Организация"
      className="max-w-2xl w-full mx-auto p-4 bg-white rounded-xl shadow"
      loading={pending}
      submitText="Создать"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField label="БИН" value={dn.company!.bin} editable={false} />
        <TextField
          label="Наименование"
          value={values.name}
          name="name"
          onChange={onChange}
          error={!!errors.name}
          helperText={errors.name}
        />
        <OkedSelectTrigger
          label="Категория"
          value={''}
          onChange={onChangeOked}
          name="oked"
          placeholder="ХХХХХХХХХХХ"
          helperText={errors.okeds || undefined}
          error={!!errors.okeds}
        />
        <StyledOkedListBlock
          list={values.okeds || []}
          onClickClear={onClearOked}
        />
        <div className="flex items-center mt-2">
          <Checkbox
            label="Поставщик"
            checked={values.isSupplier}
            onChange={onSupplierChange}
          />
        </div>
      </div>
    </Form>
  );
}

export default memo(CompanyForm);
